import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonVariant
} from "shared/components";
import { Step } from "../types";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { useState } from "react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Loader } from "shared/components/Loader";
import {
  ChooseTemplateScreen,
  type TemplateRouteQuestion
} from "./ChooseTemplateScreen";

export const StartApplicationSplash = ({
  applicationId,
  applicantId,
  onSuccess,
  applicantFullName,
  onClose
}: {
  applicationId: number;
  applicantId: number;
  onSuccess: () => void;
  applicantFullName: string;
  onClose: () => void;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templateRoute, setTemplateRoute] = useState<string>();
  const [templateChosen, setTemplateChosen] = useState(false);

  const templateRouteMeta = useQuery({
    queryKey: ["TEMPLATE_ROUTE_QUESTION", applicationId, applicantId],
    queryFn: () =>
      instance
        .get<{
          template_route_question: TemplateRouteQuestion | null;
        }>(
          `/portal/v1/applications/${applicationId}/applicants/${applicantId}/template_route_question`
        )
        .then((res) => res.data.template_route_question),
    enabled: Boolean(applicationId) && Boolean(applicantId)
  });

  const startApplicationMeta = useMutation({
    mutationKey: ["startApplication", applicationId, applicantId],
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/start_application`,
        templateRoute === undefined
          ? undefined
          : { template_route: templateRoute }
      )
  });

  const verificationsMeta = useQuery({
    queryKey: ["verificationsMeta", applicationId, applicantId],
    queryFn: () =>
      instance
        .get(
          `/portal/v1/applications/${applicationId}/verifications?applicant_id=${applicantId}`
        )
        .then((res) => {
          return res.data;
        }),

    enabled: Boolean(applicationId) && Boolean(applicantId)
  });

  const handleStartApplication = async () => {
    setIsSubmitting(true);
    try {
      await startApplicationMeta.mutateAsync();
      // Answering the template question may have rebuilt the verifications.
      const { data: verifications } = await verificationsMeta.refetch();
      const rentalQualification = verifications?.verifications
        ?.find((verification) => verification.name === "terms_and_conditions")
        ?.steps.find((step) => step.name === "terms_and_conditions_step") as
        | Step
        | undefined;

      if (rentalQualification) {
        const autoSkipData = { accepted: true };

        await instance.patch(
          `/portal/v1/applications/${applicationId}/verifications/${rentalQualification.verification_id}/steps/${rentalQualification.name}?applicant_id=${applicantId}`,
          { data: autoSkipData }
        );
      }

      onSuccess();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (templateRouteMeta.isPending)
    return (
      <div className="h-full flex items-center justify-center animate-appear">
        <Loader />
      </div>
    );

  // Without the question the start would be refused (the answer is required), so never fall through to the splash.
  if (templateRouteMeta.isError)
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 px-12 text-center">
        <p className="text-base font-medium text-content-1000">
          {getErrorMessage(templateRouteMeta.error)}
        </p>
        <Button
          variant={ButtonVariant.BASE}
          onClick={() => templateRouteMeta.refetch()}
        >
          Try again
        </Button>
      </div>
    );

  if (templateRouteMeta.data && !templateChosen)
    return (
      <ChooseTemplateScreen
        templateRoute={templateRouteMeta.data}
        answer={templateRoute}
        onChange={setTemplateRoute}
        onContinue={() => setTemplateChosen(true)}
        onClose={onClose}
      />
    );

  return (
    <div className="relative h-full">
      <div className="h-[72px] border-b border-alpha-100 flex justify-end items-center px-2">
        <IconButton
          HugeIcon={Cancel01Icon}
          variant={IconButtonVariant.TEXT_BASE}
          onClick={() => {
            onClose();
          }}
        />
      </div>
      <div className="bg-background-800 px-4 pt-10">
        <img
          src="/assets/manual-create-applicant-splash.png"
          alt="applicant experience preview"
          className="max-w-[490px] mx-auto w-full"
        />
      </div>
      <div className="px-12 py-8 overflow-y-auto max-h-[calc(100vh-532px)]">
        <div className="border-b border-alpha-40 pb-4 mb-4">
          <h1 className="mb-1 text-2xl font-bold text-content-1000">
            Rental application for {applicantFullName}
          </h1>
          <p className="text-sm text-content-400">Applicant</p>
        </div>

        <div className="text-base text-content-400">
          <p className="mb-4">
            You are about to complete a rental application for{" "}
            {applicantFullName}.
          </p>

          <p className="mb-4">
            Please gather all the necessary information required for the
            application, including contact details, current address, SSN,
            identification documents, income, and housing history.
          </p>

          <p>Please note:</p>

          <ul className="list-disc pl-6 flex flex-col gap-4">
            <li className="mt-4">
              The applicant will need to sign a disclosure to run the screening
              services.
            </li>

            <li>
              Income verification is only available through supporting
              documents.
            </li>

            <li>
              Payment for the application can be made through the portal using
              the applicant&apos;s credit card details. A payment request can be
              sent to the applicant, or you can confirm that the applicant has
              paid or will pay in person and screening service fee will be
              charged to the bank account linked to the property
            </li>
          </ul>
        </div>
      </div>

      <div className="h-20 absolute bottom-0 left-0 right-0 border-t border-alpha-40 w-full flex justify-end px-6 py-4 bg-white rounded-b-lg">
        <Button
          className="w-[125px]"
          variant={ButtonVariant.PRIMARY}
          loading={isSubmitting}
          onClick={() => {
            handleStartApplication();
          }}
          disabled={verificationsMeta.isPending || verificationsMeta.isError}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
